/*
 * Teacher-facing "Day in review" history. Rendered as a top-level tab in
 * ClassroomDetail (peer of Settings) — never on the live student screens.
 * Two-column: a day picker (month calendar + recent list) on the left, the
 * selected day's review on the right. Collapses to one column when narrow.
 */

.panel {
  padding: 18px 22px;
}

.header {
  margin-bottom: 14px;
}

.headerText {
  min-width: 0;
}

.heading {
  font-family: var(--token-font-display, var(--token-font-ui));
  font-size: 22px;
  font-weight: 700;
  margin: 0 0 4px;
}

.description {
  font-family: var(--token-font-ui);
  font-size: 13px;
  line-height: 1.45;
  color: rgba(40, 40, 50, 0.7);
  margin: 0;
  max-width: 60ch;
}

.layout {
  display: grid;
  grid-template-columns: minmax(260px, 320px) 1fr;
  gap: 20px;
  align-items: start;
}

@media (max-width: 760px) {
  .layout {
    grid-template-columns: 1fr;
  }
}

/* ---------- left: picker ---------- */

.picker {
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
}

.calendar {
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 14px;
  padding: 12px;
  background: rgba(255, 255, 255, 0.4);
}

.calHead {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.calTitle {
  font-family: var(--token-font-ui);
  font-size: 14px;
  font-weight: 700;
}

.calNav {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  background: transparent;
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  color: inherit;
}

.calNav:hover {
  background: rgba(0, 0, 0, 0.05);
}

.calGrid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
}

.calWeekday {
  text-align: center;
  font-family: var(--token-font-ui);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.3px;
  color: rgba(40, 40, 50, 0.45);
  padding-bottom: 4px;
}

.calBlank {
  aspect-ratio: 1;
}

.calDay {
  position: relative;
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid transparent;
  border-radius: 8px;
  background: transparent;
  font-family: var(--token-font-ui);
  font-size: 13px;
  color: inherit;
  cursor: pointer;
}

.calDay:hover:not(:disabled) {
  background: rgba(0, 0, 0, 0.06);
}

.calDay:disabled {
  opacity: 0.3;
  cursor: default;
}

.calDayToday {
  border-color: rgba(60, 110, 200, 0.5);
  font-weight: 700;
}

.calDaySelected {
  background: var(--token-accent, #3c6ec8);
  color: #fff;
}

.calDaySelected:hover:not(:disabled) {
  background: var(--token-accent, #3c6ec8);
}

.dot {
  position: absolute;
  bottom: 3px;
  left: 50%;
  transform: translateX(-50%);
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: var(--token-accent, #3c6ec8);
}

.calDaySelected .dot {
  background: #fff;
}

/* ---------- recent list ---------- */

.recent {
  min-width: 0;
}

.recentTitle {
  font-family: var(--token-font-ui);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  color: rgba(40, 40, 50, 0.5);
  margin: 0 0 8px;
}

.recentList {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  max-height: 160px;
  overflow-y: auto;
}

.recentItem {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  border-radius: 999px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  background: transparent;
  font-family: var(--token-font-ui);
  font-size: 12px;
  cursor: pointer;
  color: inherit;
}

.recentItem:hover {
  background: rgba(0, 0, 0, 0.05);
}

.recentItemActive {
  background: var(--token-accent, #3c6ec8);
  color: #fff;
  border-color: transparent;
}

.todayTag {
  font-size: 10px;
  font-weight: 700;
  opacity: 0.7;
}

/* ---------- right: review ---------- */

.review {
  min-width: 0;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 14px;
  padding: 16px 18px;
  background: rgba(255, 255, 255, 0.4);
}

.reviewHead {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.reviewDate {
  font-family: var(--token-font-ui);
  font-size: 16px;
  font-weight: 700;
  margin: 0;
}

.exportBtn {
  font-size: 12px;
  font-weight: 600;
  padding: 6px 14px;
  flex-shrink: 0;
}

.sections {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.section {
  min-width: 0;
}

.sectionTitle {
  font-family: var(--token-font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: rgba(40, 40, 50, 0.5);
  margin: 0 0 4px;
}

.sectionBody {
  font-family: var(--token-font-ui);
  font-size: 14px;
  line-height: 1.5;
  margin: 0;
  white-space: pre-wrap;
}

.sectionAnswer {
  font-family: var(--token-font-ui);
  font-size: 13px;
  line-height: 1.45;
  margin: 4px 0 0;
  color: rgba(40, 40, 50, 0.7);
}

.list {
  margin: 0;
  padding-left: 18px;
  font-family: var(--token-font-ui);
  font-size: 14px;
  line-height: 1.55;
}

.searchList {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.searchLink {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-family: var(--token-font-ui);
  font-size: 14px;
  color: var(--token-accent, #3c6ec8);
  text-decoration: none;
}

.searchLink:hover {
  text-decoration: underline;
}

.searchKind {
  font-size: 12px;
  opacity: 0.8;
}

.empty {
  font-family: var(--token-font-ui);
  font-size: 13px;
  color: rgba(40, 40, 50, 0.55);
  margin: 0;
  padding: 8px 0;
}
